import { api } from '../lib/api';
import { ViewerConnection } from '../lib/webrtc/viewer';
import { generateQRCode } from '../lib/qrcode';
import { getStreamUrl } from '../lib/network';

export async function renderEventView(container: HTMLElement, params?: Record<string, string>): Promise<void> {
  const eventId = params?.['$1'];
  if (!eventId) {
    container.innerHTML = '<div class="container"><h1>Event not found</h1></div>';
    return;
  }

  // Check authentication
  const user = await api.getCurrentUser();
  if (!user) {
    window.location.href = '/';
    return;
  }

  // Get event details
  let event;
  try {
    event = await api.getEvent(eventId);
  } catch (error: any) {
    if (error.status === 403) {
      container.innerHTML = '<div class="container"><h1>Access denied</h1><p>You are not the creator of this event.</p></div>';
    } else if (error.status === 404) {
      container.innerHTML = '<div class="container"><h1>Event not found</h1></div>';
    } else {
      container.innerHTML = '<div class="container"><h1>Error loading event</h1></div>';
    }
    return;
  }

  if (!event.isActive) {
    container.innerHTML = `
      <div class="container" style="text-align: center; padding-top: 4rem;">
        <h1>Event Ended</h1>
        <p style="color: var(--color-text-muted); margin-top: 1rem;">This event has ended and is no longer accepting streams.</p>
        <a href="/dashboard" style="color: var(--color-primary); display: inline-block; margin-top: 2rem;">Back to Dashboard</a>
      </div>
    `;
    return;
  }

  const streamUrl = getStreamUrl(event.id);
  const qrDataUrl = await generateQRCode(streamUrl);

  container.innerHTML = `
    <div class="event-view">
      <header class="event-header">
        <div class="header-left">
          <a href="/dashboard" class="back-link">&larr; Back</a>
          <h1>${escapeHtml(event.name)}</h1>
          <span class="status active">Live</span>
        </div>
        <div class="header-right">
          <button class="secondary" id="show-qr-btn">Show QR</button>
          <button class="secondary" id="end-event-btn">End Event</button>
        </div>
      </header>

      <main class="event-main">
        <div class="video-grid" id="video-grid">
          <div class="empty-state" id="empty-state">
            <div class="qr-preview">
              <img src="${qrDataUrl}" alt="QR Code" width="200" height="200">
            </div>
            <h2>Waiting for streams...</h2>
            <p>Share the QR code or link to let participants stream to you</p>
            <p class="stream-url">${streamUrl}</p>
            <button class="secondary" id="copy-link-btn">Copy Link</button>
          </div>
        </div>
      </main>
    </div>

    <style>
      .event-view {
        min-height: 100vh;
        display: flex;
        flex-direction: column;
      }

      .event-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 1rem 2rem;
        background: var(--color-surface);
        border-bottom: 1px solid var(--color-border);
        flex-wrap: wrap;
        gap: 1rem;
      }

      .header-left {
        display: flex;
        align-items: center;
        gap: 1rem;
      }

      .back-link {
        color: var(--color-text-muted);
        text-decoration: none;
      }

      .back-link:hover {
        color: var(--color-text);
      }

      .header-left h1 {
        font-size: 1.25rem;
      }

      .status {
        display: inline-flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0.25rem 0.75rem;
        border-radius: 9999px;
        font-size: 0.75rem;
        font-weight: 500;
        background: rgba(34, 197, 94, 0.1);
        color: var(--color-success);
      }

      .status::before {
        content: '';
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: currentColor;
        animation: pulse 2s infinite;
      }

      @keyframes pulse {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.5; }
      }

      .header-right {
        display: flex;
        gap: 0.5rem;
      }

      .event-main {
        flex: 1;
        padding: 1rem;
      }

      .video-grid {
        display: grid;
        gap: 1rem;
        grid-template-columns: 1fr;
        height: calc(100vh - 80px);
      }

      @media (min-width: 640px) {
        .video-grid:not(.empty) { grid-template-columns: repeat(2, 1fr); }
      }
      @media (min-width: 1024px) {
        .video-grid:not(.empty) { grid-template-columns: repeat(3, 1fr); }
      }
      @media (min-width: 1440px) {
        .video-grid:not(.empty) { grid-template-columns: repeat(4, 1fr); }
      }

      .empty-state {
        grid-column: 1 / -1;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        text-align: center;
        padding: 2rem;
      }

      .qr-preview {
        background: white;
        padding: 1rem;
        border-radius: var(--radius-lg);
        margin-bottom: 1.5rem;
      }

      .empty-state h2 {
        margin-bottom: 0.5rem;
      }

      .empty-state p {
        color: var(--color-text-muted);
      }

      .stream-url {
        margin-top: 1rem;
        font-family: monospace;
        font-size: 0.875rem;
        padding: 0.5rem 1rem;
        background: var(--color-surface);
        border-radius: var(--radius);
      }

      #copy-link-btn {
        margin-top: 1rem;
      }

      .stream-tile {
        position: relative;
        background: var(--color-surface);
        border-radius: var(--radius-lg);
        overflow: hidden;
        aspect-ratio: 16 / 9;
      }

      .stream-tile video {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }

      .stream-tile .overlay {
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        padding: 0.75rem;
        background: linear-gradient(transparent, rgba(0, 0, 0, 0.8));
        display: flex;
        justify-content: space-between;
        align-items: center;
      }

      .stream-tile .label {
        font-size: 0.875rem;
        font-weight: 500;
      }

      .stream-tile .controls {
        display: flex;
        gap: 0.5rem;
      }

      .stream-tile .controls button {
        padding: 0.5rem;
        background: rgba(255, 255, 255, 0.1);
        border-radius: var(--radius);
        font-size: 0.875rem;
      }

      .stream-tile .controls button:hover {
        background: rgba(255, 255, 255, 0.2);
      }

      .stream-tile .connection-status {
        position: absolute;
        top: 0.5rem;
        right: 0.5rem;
        padding: 0.25rem 0.5rem;
        border-radius: 9999px;
        font-size: 0.625rem;
        font-weight: 500;
        display: flex;
        align-items: center;
        gap: 0.25rem;
      }

      .stream-tile .connection-status::before {
        content: '';
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: currentColor;
      }

      .stream-tile .connection-status.connected {
        background: rgba(34, 197, 94, 0.2);
        color: var(--color-success);
      }

      .stream-tile .connection-status.connecting {
        background: rgba(245, 158, 11, 0.2);
        color: var(--color-warning);
      }

      .qr-modal {
        position: fixed;
        inset: 0;
        background: rgba(0, 0, 0, 0.8);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 100;
      }

      .qr-modal-content {
        background: var(--color-surface);
        padding: 2rem;
        border-radius: var(--radius-lg);
        text-align: center;
        max-width: 400px;
      }

      .qr-modal-content h3 {
        margin-bottom: 1rem;
      }

      .qr-modal-content img {
        margin-bottom: 1rem;
      }

      .qr-modal-content .modal-actions {
        display: flex;
        gap: 0.5rem;
        margin-top: 1rem;
      }

      .qr-modal-content .modal-actions button {
        flex: 1;
      }
    </style>
  `;

  const videoGrid = document.getElementById('video-grid')!;
  const emptyState = document.getElementById('empty-state');
  const streams = new Map<string, { video: HTMLVideoElement; tile: HTMLElement }>();

  // Initialize WebRTC viewer connection
  const viewer = new ViewerConnection(eventId, {
    onStreamAdded: (streamId, label, mediaStream) => {
      if (emptyState) {
        emptyState.remove();
        videoGrid.classList.remove('empty');
      }

      const tile = createStreamTile(streamId, label, mediaStream);
      videoGrid.appendChild(tile.element);
      streams.set(streamId, { video: tile.video, tile: tile.element });
    },
    onStreamRemoved: (streamId) => {
      const stream = streams.get(streamId);
      if (stream) {
        stream.tile.remove();
        streams.delete(streamId);
      }

      if (streams.size === 0 && emptyState) {
        videoGrid.innerHTML = '';
        videoGrid.appendChild(emptyState);
        videoGrid.classList.add('empty');
      }
    },
    onConnectionStateChange: (streamId, state) => {
      const stream = streams.get(streamId);
      if (stream) {
        const statusEl = stream.tile.querySelector('.connection-status');
        if (statusEl) {
          statusEl.className = `connection-status ${state === 'connected' ? 'connected' : 'connecting'}`;
          statusEl.textContent = state === 'connected' ? 'Live' : 'Connecting';
        }
      }
    },
  });

  viewer.connect();

  // Event handlers
  document.getElementById('copy-link-btn')?.addEventListener('click', async () => {
    await navigator.clipboard.writeText(streamUrl);
    const btn = document.getElementById('copy-link-btn') as HTMLButtonElement;
    btn.textContent = 'Copied!';
    setTimeout(() => btn.textContent = 'Copy Link', 2000);
  });

  document.getElementById('show-qr-btn')?.addEventListener('click', () => {
    showQRModal(event.name, streamUrl, qrDataUrl);
  });

  document.getElementById('end-event-btn')?.addEventListener('click', async () => {
    if (confirm('Are you sure you want to end this event? All streamers will be disconnected.')) {
      await api.deleteEvent(eventId);
      viewer.disconnect();
      window.location.href = '/dashboard';
    }
  });

  // Cleanup on navigation
  window.addEventListener('beforeunload', () => viewer.disconnect());
}

function createStreamTile(streamId: string, label: string, mediaStream: MediaStream): { element: HTMLElement; video: HTMLVideoElement } {
  const tile = document.createElement('div');
  tile.className = 'stream-tile';
  tile.dataset.streamId = streamId;

  const video = document.createElement('video');
  video.autoplay = true;
  video.playsInline = true;
  video.muted = false;
  video.srcObject = mediaStream;

  tile.innerHTML = `
    <div class="connection-status connecting">Connecting</div>
    <div class="overlay">
      <span class="label">${escapeHtml(label)}</span>
      <div class="controls">
        <button class="mute-btn" title="Mute">🔊</button>
        <button class="fullscreen-btn" title="Fullscreen">⛶</button>
      </div>
    </div>
  `;

  tile.prepend(video);

  tile.querySelector('.mute-btn')?.addEventListener('click', () => {
    video.muted = !video.muted;
    const btn = tile.querySelector('.mute-btn')!;
    btn.textContent = video.muted ? '🔇' : '🔊';
  });

  tile.querySelector('.fullscreen-btn')?.addEventListener('click', () => {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      tile.requestFullscreen();
    }
  });

  return { element: tile, video };
}

function showQRModal(name: string, streamUrl: string, qrDataUrl: string): void {
  const modal = document.createElement('div');
  modal.className = 'qr-modal';
  modal.innerHTML = `
    <div class="qr-modal-content">
      <h3>${escapeHtml(name)}</h3>
      <img src="${qrDataUrl}" alt="QR Code" width="256" height="256">
      <p style="color: var(--color-text-muted); font-size: 0.875rem; word-break: break-all;">${streamUrl}</p>
      <div class="modal-actions">
        <button class="secondary copy-btn">Copy Link</button>
        <button class="primary close-btn">Close</button>
      </div>
    </div>
  `;

  document.body.appendChild(modal);

  modal.querySelector('.close-btn')?.addEventListener('click', () => modal.remove());
  modal.querySelector('.copy-btn')?.addEventListener('click', async () => {
    await navigator.clipboard.writeText(streamUrl);
    const btn = modal.querySelector('.copy-btn') as HTMLButtonElement;
    btn.textContent = 'Copied!';
    setTimeout(() => btn.textContent = 'Copy Link', 2000);
  });
  modal.addEventListener('click', (e) => {
    if (e.target === modal) modal.remove();
  });
}

function escapeHtml(str: string): string {
  const div = document.createElement('div');
  div.textContent = str;
  return div.innerHTML;
}
